import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'

// Zinsser guide: monochromatic grayscale for all badges
const badgeVariants = cva(
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-light transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-foreground text-background',
        secondary: 'bg-muted text-muted-foreground',
        outline: 'border border-border text-foreground',
        // The Five Spaces (Sovereign Entity Framework) - all monochromatic per Zinsser guide
        entity: 'bg-muted text-muted-foreground',
        assets: 'bg-muted text-muted-foreground',
        operations: 'bg-muted text-muted-foreground',
        develop: 'bg-muted text-muted-foreground',
        identity: 'bg-muted text-muted-foreground',
        // Legacy domains (backward compatibility)
        design: 'bg-muted text-muted-foreground',
        operate: 'bg-muted text-muted-foreground',
        market: 'bg-muted text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'secondary',
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

// Type badge with auto-styling - monochromatic
export function TypeBadge({ type }: { type: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-light',
        'bg-muted text-muted-foreground'
      )}
    >
      {type.replace('_', ' ')}
    </span>
  )
}

// Space badge with auto-styling (Sovereign Entity Framework)
export function DomainBadge({ domain }: { domain: string | null }) {
  if (!domain) return null
  const variant = domain.toLowerCase() as 'entity' | 'assets' | 'operations' | 'develop' | 'identity'
  return <Badge variant={variant}>{domain.toLowerCase()}</Badge>
}

// Phase badge with auto-styling (deprecated - phase is no longer used)
export function PhaseBadge({ phase }: { phase: string | null | undefined }) {
  if (!phase) return null
  // Phase variants no longer exist, use default
  return <Badge variant="default">{phase}</Badge>
}
